import React, { useMemo } from 'react';
import {
    Box, Typography, Paper, Card, CardContent, Grid, Stack,
    Chip, List, ListItem, ListItemText, ListItemIcon,
    LinearProgress, Divider
} from '@mui/material';
import {
    Public as PublicIcon,
    Schedule as ScheduleIcon,
    FlightTakeoff as FlightIcon
} from '@mui/icons-material';

interface TravelForm {
    id: number;
    staffName: string;
    email: string;
    projectNumber: string;
    projectName: string;
    client: string;
    destination: string[];
    travelDateFrom: string;
    travelDateTo: string;
    status: string;
    department: string;
    submittedDate?: string | null;
    lastModified: string;
    // Allow additional properties for flexibility
    accommodations?: number;
    travelSegments?: number;
    completionPercentage?: number;
    [key: string]: any;
}

interface TravelAnalyticsProps {
    travelForms: TravelForm[];
    timeRange?: 'month' | 'quarter' | 'year';
}

const TravelAnalytics: React.FC<TravelAnalyticsProps> = ({
    travelForms,
    timeRange = 'month'
}) => {
    const analytics = useMemo(() => {
        const now = new Date();
        const startOfPeriod = new Date();

        // Set the start date based on time range
        if (timeRange === 'month') {
            startOfPeriod.setMonth(now.getMonth() - 1);
        } else if (timeRange === 'quarter') {
            startOfPeriod.setMonth(now.getMonth() - 3);
        } else {
            startOfPeriod.setFullYear(now.getFullYear() - 1);
        }

        const periodForms = travelForms.filter(form => {
            const travelDate = new Date(form.travelDateFrom);
            return travelDate >= startOfPeriod;
        });



        // Destination popularity
        const destinationStats = travelForms.reduce((acc, form) => {
            form.destination.forEach(dest => {
                acc[dest] = (acc[dest] || 0) + 1;
            });
            return acc;
        }, {} as Record<string, number>);

        // Client activity
        const clientStats = travelForms.reduce((acc, form) => {
            acc[form.client] = (acc[form.client] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        // Status distribution
        const statusStats = travelForms.reduce((acc, form) => {
            acc[form.status] = (acc[form.status] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        // Monthly trend (for chart data)
        const monthlyTrend = [];
        for (let i = 5; i >= 0; i--) {
            const monthStart = new Date();
            monthStart.setMonth(now.getMonth() - i);
            monthStart.setDate(1);
            const monthEnd = new Date(monthStart);
            monthEnd.setMonth(monthStart.getMonth() + 1);
            monthEnd.setDate(0);

            const monthForms = travelForms.filter(form => {
                const travelDate = new Date(form.travelDateFrom);
                return travelDate >= monthStart && travelDate <= monthEnd;
            }).length;

            monthlyTrend.push({
                month: monthStart.toLocaleDateString('en', { month: 'short' }),
                count: monthForms
            });
        }

        // Travel duration analysis
        const avgTravelDuration = travelForms.reduce((sum, form) => {
            const from = new Date(form.travelDateFrom);
            const to = new Date(form.travelDateTo);
            const duration = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
            return sum + duration;
        }, 0) / travelForms.length;



        return {
            totalForms: travelForms.length,
            periodForms: periodForms.length,
            destinationStats,
            clientStats,
            statusStats,
            monthlyTrend,
            avgTravelDuration,
            topDestinations: Object.entries(destinationStats)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5),
            topClients: Object.entries(clientStats)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
        };
    }, [travelForms, timeRange]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return '#4caf50';
            case 'submitted': return '#2196f3';
            case 'draft': return '#ff9800';
            default: return '#757575';
        }
    };



    return (
        <Grid container spacing={3}>
            {/* Key Metrics */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                            <FlightIcon color="primary" />
                            <Typography variant="h4" color="primary">
                                {analytics.totalForms}
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Total Travel Forms
                        </Typography>
                        <Typography variant="caption" color="success.main">
                            {analytics.periodForms} this {timeRange}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                            <ScheduleIcon color="info" />
                            <Typography variant="h4" color="info.main">
                                {Math.round(analytics.avgTravelDuration)}
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Avg Trip Duration (days)
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>





            {/* Top Destinations */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <PublicIcon color="primary" />
                            <Typography variant="h6">
                                Top Destinations
                            </Typography>
                        </Stack>
                        <List dense>
                            {analytics.topDestinations.map(([destination, count], index) => (
                                <ListItem key={destination} sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            #{index + 1}
                                        </Typography>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={destination}
                                        secondary={`${count} trips`}
                                    />
                                    <Box sx={{ width: 80 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={(count / Math.max(...analytics.topDestinations.map(([, c]) => c))) * 100}
                                            sx={{ height: 6, borderRadius: 3 }}
                                        />
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            </Grid>



            {/* Status Distribution */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Form Status Distribution
                        </Typography>
                        <Stack spacing={2}>
                            {Object.entries(analytics.statusStats).map(([status, count]) => (
                                <Box key={status}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                        <Chip
                                            label={status.toUpperCase()}
                                            size="small"
                                            sx={{
                                                bgcolor: getStatusColor(status),
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                        <Typography variant="body2">
                                            {count} forms ({Math.round((count / analytics.totalForms) * 100)}%)
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(count / analytics.totalForms) * 100}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            bgcolor: 'grey.200',
                                            '& .MuiLinearProgress-bar': {
                                                bgcolor: getStatusColor(status)
                                            }
                                        }}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            {/* Monthly Trend */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Travel Trend (Last 6 Months)
                        </Typography>
                        <Stack spacing={1}>
                            {analytics.monthlyTrend.map(({ month, count }) => (
                                <Box key={month}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                        <Typography variant="body2">
                                            {month}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {count} trips
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={count > 0 ? (count / Math.max(...analytics.monthlyTrend.map(t => t.count))) * 100 : 0}
                                        sx={{ height: 6, borderRadius: 3 }}
                                    />
                                </Box>
                            ))}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" color="text.secondary">
                            <strong>Insights:</strong> {analytics.monthlyTrend.reduce((sum, t) => sum + t.count, 0)} total trips in the last 6 months
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default TravelAnalytics;
